import { ActionModal, CheckboxSelectPaged, FormLayout } from '@kcftech/design-system';
import { ManufacturerApproveRequest } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useNotificationContext } from 'contexts/NotificationContext';
import { sdClient } from 'contexts/SDKClientContext';
import { useInfiniteManufacturersDataSource } from 'custom_hooks/data_hooks/data_source/manufacturersDataSource';
import { useInfiniteManufacturersDetailDataSource } from 'custom_hooks/data_hooks/data_source/manufacturersDetailDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { ManufacturerMaxLength } from 'pages/Settings/ThirdPartySensorModels/shared';
import { StyledTextInput } from 'pages/Settings/ThirdPartySensorModels/styled';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { RequiredMessage } from 'shared/form/RequiredMessage';
import { any, uniqueBy } from 'utils/array';
import { pluralize } from 'utils/string';

export type CreateAndApproveManufacturerForm = Readonly<{
    name: string;
    additionalStrings: string[];
}>;

export type CreateAndApproveManufacturerModalProps = Readonly<{
    defaultManufacturerName?: string;
    onClose: (updated?: boolean) => void;
}>;

export function CreateAndApproveManufacturerModal({
    defaultManufacturerName,
    onClose,
}: CreateAndApproveManufacturerModalProps) {
    const { showToast } = useNotificationContext();

    const {
        control,
        register,
        handleSubmit,
        watch,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
    } = useForm<CreateAndApproveManufacturerForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            name: defaultManufacturerName,
            additionalStrings: [],
        },
    });
    const { name: manufacturerName, additionalStrings } = watch();

    const { mutate: createAndApproveManufacturer, isPending: isSaving } = useMutation({
        meta: { name: 'create-and-approve-manufacturer' },
        mutationFn: async (data: CreateAndApproveManufacturerForm) => {
            await sdClient.createAndApproveManufacturer({
                body: new ManufacturerApproveRequest({
                    Name: data.name,
                    AdditionalStrings: data.additionalStrings,
                }),
            });
        },

        onSuccess: () => {
            showToast({
                type: 'success',
                content: 'Successfully created Approved Manufacturer',
            });
            onClose(true);
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to create Approved Manufacturer',
            }),
    });

    const manufacturersSelectDataSource = useInfiniteManufacturersDataSource({
        name: 'manufacturers-approve-checkbox-select',
        defaultFilter: { isApproved: false, nameSearch: '' },
    });

    const manufacturersAffectedDataSource = useInfiniteManufacturersDetailDataSource({
        name: 'manufacturers-approve-affected',
        defaultFilter: { isApproved: false, nameSearch: '' },
        defaultSort: [{ field: 'Name', dir: 'asc' }],
    });
    const { setFilter: setAffectedFilter } = manufacturersAffectedDataSource;
    useDrainDataSource(manufacturersAffectedDataSource);

    useEffect(() => {
        setAffectedFilter((cur) => ({
            ...cur,
            exactCaseInsensitiveStrings: [manufacturerName, ...additionalStrings],
        }));
    }, [additionalStrings, manufacturerName, setAffectedFilter]);

    const onSave = (data: CreateAndApproveManufacturerForm) => {
        createAndApproveManufacturer(data);
    };

    const additionalStringsOptions = useMemo(
        () =>
            uniqueBy(
                manufacturersSelectDataSource.results?.map((m) => m.Name) ?? [],
                (v) => v.toLowerCase(),
            ).map((s) => ({
                value: s,
                label: s,
            })),
        [manufacturersSelectDataSource.results],
    );

    return (
        <ActionModal
            height={600}
            title="Approve Third-Party Sensor"
            actionButtonText="Approve"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting) && !isSaving
            }
            onAction={handleSubmit(onSave)}
            onClose={onClose}
        >
            <FormLayout>
                <RequiredMessage />
                <ValidatableLabel label="Manufacturer Name" required error={errors.name}>
                    <StyledTextInput
                        placeholder="Enter a name"
                        $lightBackground
                        {...register('name', {
                            required: 'Name is required.',
                            validate: (value) =>
                                !value.trim()
                                    ? 'Please enter a manufacturer name.'
                                    : undefined,
                            maxLength: {
                                value: ManufacturerMaxLength,
                                message: `Manufacturer Name cannot exceed ${ManufacturerMaxLength} characters.`,
                            },
                        })}
                    />
                </ValidatableLabel>
                <Controller
                    control={control}
                    name="additionalStrings"
                    render={({ field: { onChange } }) => (
                        <CheckboxSelectPaged
                            label="Additional Names to Convert"
                            hasNext={manufacturersSelectDataSource.hasNext ?? false}
                            isLoading={manufacturersSelectDataSource.isLoading}
                            loadNext={manufacturersSelectDataSource.loadNext}
                            options={additionalStringsOptions}
                            placeholder="Manufacturer Name or Code..."
                            inputValue={manufacturersSelectDataSource.filter.nameSearch}
                            onInputChange={(nameSearch) => {
                                manufacturersSelectDataSource.setFilter((cur) => ({
                                    ...cur,
                                    nameSearch,
                                }));
                            }}
                            onChange={(newValues) =>
                                onChange(newValues.map((v) => v.value))
                            }
                        />
                    )}
                />
                <div>
                    <p>
                        This operation will replace{' '}
                        <strong>
                            {manufacturersAffectedDataSource.paging.totalResults}
                        </strong>{' '}
                        Corporation-level{' '}
                        {pluralize(
                            manufacturersAffectedDataSource.paging.totalResults ?? 0,
                            'manufacturer',
                        )}{' '}
                        with a single Global Manufacturer.
                    </p>
                    {any(manufacturersAffectedDataSource.results) && (
                        <div style={{ overflowY: 'auto', height: '130px' }}>
                            <strong>Affected Manufacturers:</strong>
                            <ul style={{ margin: 0 }}>
                                {manufacturersAffectedDataSource.results.map((m) => (
                                    <li key={m.Id}>
                                        {/* I'm asserting the state of the program gdi! only */}
                                        {/* corp-level manufacturers should be returned here! */}
                                        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                                        {m.Name} ({m.Corporation!.CorporationName})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </FormLayout>
        </ActionModal>
    );
}
